'use client';
import styles from './styles.module.css';
import 'swiper/css/navigation';
import {useEffect, useRef, useState} from 'react';

import {motion} from 'framer-motion';
import globalDomains, {globalAPPScheme} from '@/utils/global-domains';
import {SoopUiSwiper} from '@/components/ui';
import MyProfileImg from '@/components/my/common/item/MyProfileImg';

import SubscribeEventApi from '@/api/events/new-subscribe';

import {
  loginState,
  inAppStore,
  handleLogin,
  handleUserInfo as loginUserInfo,
  handleCurrentFormatDate,
} from '@/utils/event/util';

const NewSubscribe = ({onBodyId}) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activeGuideLines, setActiveGuideLines] = useState(-1);
  const subscriptionEventSectionRef = useRef([]);

  const [userNick, setUserNick] = useState('');
  const [userId, setUserId] = useState('');

  const [isEarlyExit, setIsEarlyExit] = useState(false);

  const [eventInfo, setEventInfo] = useState({});

  const [layerOpen, setLayerOpen] = useState(false);
  const [layerMessage, setLayerMessage] = useState('');

  const [isClickTab, setIsClickTab] = useState(false);

  useEffect(() => {
    /**
     * 초기 로드시 body id 셋팅
     */
    onBodyId && onBodyId(styles.body);
    setLoading(true);
  }, [onBodyId]);

  // 로그인 정보 셋팅
  useEffect(() => {
    if (loginState) {
      const {userId, userNickname} = loginUserInfo();
      setUserNick(userNickname);
      setUserId(userId);
    }
  }, [loginState]);

  // 이벤트 참여대상 여부
  const handleGetEventTargetUserCheck = async () => {
    const responseInfo = await SubscribeEventApi.subscribeRenewalEventInfo(
      'ChkUserSubscriptionEvent',
    );

    return {
      checkResultCode: responseInfo?.data.retcode || 0,
      message: responseInfo?.data.errmsg || '',
    };
  };

  // 참여 대상자
  const handleEventSubscriptionStreamerCount = async () => {
    const responseInfo = await SubscribeEventApi.subscribeRenewalEventInfo(
      'GetSubscriptionEventNewStreamerCnt',
    );
    return {
      baseDate:
        responseInfo?.data.basedate ||
        handleCurrentFormatDate('YYYY.MM.DD HH:mm'),
      subscriptionNewCount: responseInfo?.data.streamercnt || 0,
    };
  };

  useEffect(() => {
    const fetchEventInfo = async () => {
      if (!loading && loginState) {
        /**
         * 3001 : 신청 마감
         * 3002: 이미 신청 함
         * 3003: 자동결제 이력이 있어 이벤트 참여 불가
         * 3999: 이벤트 종료
         */

        const {checkResultCode, message = ''} =
          await handleGetEventTargetUserCheck(); // 이벤트 참여 가능 할떄만 count 값 호출
        if ([3001, 3002, 3003, 3999].includes(checkResultCode)) {
          if (checkResultCode === 3001) {
            setIsEarlyExit(true);
          }
          // 메세지 등록
          setLayerMessage(message);
        }

        const resultEvent = await handleEventSubscriptionStreamerCount();
        setEventInfo(resultEvent);
      }
    };

    fetchEventInfo();
  }, [loginState, loading]);

  /**
   * 스크롤 시 탭 활성화 로직
   */
  useEffect(() => {
    const handleScroll = (event) => {
      if (!isClickTab) {
        const scrollPosition = window.scrollY;

        subscriptionEventSectionRef.current &&
          subscriptionEventSectionRef.current.forEach((section, index) => {
            if (section) {
              const sectionTop =
                section.getBoundingClientRect().top + window.scrollY;

              if (
                scrollPosition >= sectionTop - 92 &&
                scrollPosition < sectionTop + section.offsetHeight
              ) {
                setActiveTab(index);
              }
            }
          });
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [subscriptionEventSectionRef, isClickTab]);
  /**
   * 탭 이동
   * @param {*} event
   * @returns
   */
  const handleMoveTab = (event) => {
    setIsClickTab(true);

    let activeNo = parseInt(event.currentTarget.value);

    setActiveTab(activeNo); // 상태를 변경하는 것만 남김

    subscriptionEventSectionRef.current[activeNo] &&
      subscriptionEventSectionRef.current[activeNo].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

    setTimeout(() => {
      setIsClickTab(false);
    }, 600);

    return;
  };

  const handleActiveGuideLine = (event) => {
    event.preventDefault();
    let activeNo =
      activeGuideLines === parseInt(event.currentTarget.value)
        ? -1
        : parseInt(event.currentTarget.value);

    setActiveGuideLines(activeNo);
  };

  const handleBuySubscribe = (tier, isEvent = true) => {
    // 비로그인 시 로그인 페이지로 이동
    if (!loginState) {
      handleLogin();
      return;
    }

    const store = inAppStore();

    /**
     * 앱스토어, 플레이스토에서는 이벤트 참여 불가
     */
    if (window.afreeca.link.isWebview() && !['O', 'S'].includes(store)) {
      setLayerMessage(
        'PC, 모바일 웹, <br /> 원스토어/갤럭시스토어에서 <br /> 참여하실 수 있어요.',
      );

      setLayerOpen(true);
      return;
    }

    if (isEvent && layerMessage !== '') {
      setLayerOpen(true);
      return;
    }

    let itemId = tier === 1 ? 742561 : 742566;

    itemId = tier === 2 && !isEvent ? 742565 : itemId;

    if (!window.afreeca.link.isMobile()) {
      /**
       * tier 구분해서 각각 구독 구매 페이지로 이동
       * PC, mobile web, APP
       * PC : https://item.sooplive.co.kr/pop_search_subscription.php?itemId=742561&sys_type=web&location=item&os=win
       * Mo : https://m.afreecatv.com/itemstore/a/subscripbj?itemId=742561
       */

      let url = `${globalDomains.ITEM_AFREECATV}/pop_search_subscription.php?itemId=${itemId}&sys_type=web&location=item&os=win`;
      window.open(url, 'gift_popup', 'width=320,height=240,scrollbars=yes');
      return;
    }

    // 모바일 브라우저에서 open
    let url = `${globalDomains.M_AFREECATV}/itemstore/a/subscripbj?nDevice=2&szVersion=1&szModel=web&szOsVer=web&sys_type=webm&location=itemstore&entry_location=m_itemstore&itemId=${itemId}`;

    // 원스토어, 갤럭시 스토어일 경우 외부 브라우저로 이동
    if (['O', 'S'].includes(store)) {
      let scheme = `${globalAPPScheme}browser/external?url=${encodeURIComponent(url)}`;

      window.location.href = scheme;
      return;
    }

    window.open(url, 'gift_popup', 'width=320,height=240,scrollbars=yes');
  };
  /**
   * 1. 로그인 여부
   * 2. 프로필 이미지
   */
  return (
    loading && (
      <>
        <div id={styles.wrap}>
          <header className={styles.header}>
            <h1>
              <a
                href={`javascript:void(0)`}
                onClick={() => {
                  /**
                   * 웹뷰 -> home 이동 스킴이 없어 리로드로 적용
                   */
                  if (window.afreeca.link.isWebview()) {
                    window.location.reload();
                    return;
                  }

                  window.location.href = `${globalDomains.WWW_AFREECATV}`;
                  return;
                }}>
                SOOP
              </a>
            </h1>
            <div className={styles.user_info}>
              {loginState ? (
                <>
                  <div className={styles.user}>
                    <MyProfileImg userId={userId} disableLazy={true} />
                    <span>{userNick}</span>
                  </div>
                  <button
                    type='button'
                    className={styles.account}
                    onClick={handleLogin}>
                    로그아웃
                  </button>
                </>
              ) : (
                <button
                  type='button'
                  className={styles.account}
                  onClick={handleLogin}>
                  로그인
                </button>
              )}
            </div>
          </header>

          <div className={styles.main_visual}>
            <div className={styles.inner}>
              <motion.h2
                className={styles.visual_title}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{
                  ease: 'easeInOut',
                  duration: 0.4,
                  y: {duration: 0.4},
                }}>
                <div className={styles.blind}>
                  <span>구독서비스 개편기념</span>
                  <strong>새로운 구독 혜택 언박싱</strong>
                </div>
                <p className={styles.date}>2024.11.13 ~ 2024.12.13</p>
              </motion.h2>
              <motion.div
                className={styles.motion_box}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{
                  ease: 'easeInOut',
                  delay: 0.4,
                  duration: 0.4,
                  y: {duration: 0.4},
                }}>
                <div className={styles.blind}>
                  <ul>
                    <li>
                      <button type='button'>새로워진 구독혜택</button>
                    </li>
                    <li>
                      <button type='button'>티어2 구독 출시!</button>
                    </li>
                    <li>
                      <button type='button'>개편기념 이벤트!</button>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>

          <div className={styles.tab_area}>
            <ul>
              <li className={`${activeTab === 0 ? styles.active : ''}`}>
                <button type='button' value={0} onClick={handleMoveTab}>
                  <span>
                    2+1 구독
                    <br />
                    이벤트
                  </span>
                </button>
              </li>
              <li className={`${activeTab === 1 ? styles.active : ''}`}>
                <button type='button' value={1} onClick={handleMoveTab}>
                  <span>
                    생애 최초 구독
                    <br />
                    자동결제
                  </span>
                </button>
              </li>
              <li className={`${activeTab === 2 ? styles.active : ''}`}>
                <button type='button' value={2} onClick={handleMoveTab}>
                  <span>
                    구독 자동결제
                    <br />
                    재구독
                  </span>
                </button>
              </li>
              <li className={`${activeTab === 3 ? styles.active : ''}`}>
                <button type='button' value={3} onClick={handleMoveTab}>
                  <span>
                    신규 구독
                    <br />
                    혜택 소개
                  </span>
                </button>
              </li>
            </ul>
          </div>

          <section
            className={styles.section_1}
            ref={(element) => {
              subscriptionEventSectionRef.current[0] = element;
            }}>
            <div className={styles.inner_wrap}>
              <div className={styles.inner}>
                <motion.h3
                  className={styles.title}
                  initial={{opacity: 0, y: 50}}
                  whileInView={{opacity: 1, y: 0}}
                  viewport={{once: false}}
                  transition={{
                    ease: 'easeInOut',
                    duration: 1,
                    y: {duration: 0.5},
                  }}>
                  <div className={styles.blind}>
                    구독권이 복사가 된다고? 2+1 구독 이벤트
                    <br />
                    이벤트 기간 2명 신규 구독 때마다 티어1 1개월
                    구독선물권(30일) 1개 지급!
                  </div>
                </motion.h3>
                <motion.div
                  className={styles.gift_ticket}
                  initial={{opacity: 0, y: 50}}
                  whileInView={{opacity: 1, y: 0}}
                  viewport={{once: false}}
                  transition={{
                    ease: 'easeInOut',
                    duration: 0.5,
                    delay: 0.2,
                    y: {duration: 0.5},
                  }}>
                  <div className={styles.blind}>
                    <dl>
                      <dt>
                        티어1 1개월 구독선물권<em>2+1</em>
                      </dt>
                      <dd>
                        <strong>30</strong>일
                      </dd>
                      <dd>10,000개 한정수량</dd>
                    </dl>
                  </div>
                  <div className={styles.state_wrap}>
                    <div className={styles.info_box}>
                      <p>
                        이벤트 기간 내<br />
                        <em>신규 구독한 스트리머</em>
                      </p>
                      <span className={styles.time}>
                        *
                        {eventInfo?.baseDate ||
                          handleCurrentFormatDate('YYYY.MM.DD HH:mm')}
                        기준
                      </span>
                    </div>
                    <div className={styles.num}>
                      <strong>{eventInfo?.subscriptionNewCount}</strong>
                      <span>명</span>
                    </div>
                    {/* 여기 display on off*/}
                    <div
                      className={styles.nt_login}
                      style={{display: loginState ? 'none' : 'block'}}>
                      <p>
                        로그인 후에
                        <br />
                        확인하실 수 있어요.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
              {/* 여기 display on off*/}
              <div
                className={styles.close_wrap}
                style={{display: isEarlyExit ? 'block' : 'none'}}>
                <div className={styles.blind}>
                  <strong>이벤트가 조기 종료되었어요.</strong>
                </div>
              </div>
            </div>
          </section>

          <section
            className={styles.section_2}
            ref={(element) => {
              subscriptionEventSectionRef.current[1] = element;
            }}>
            <div className={styles.inner}>
              <motion.h3
                className={styles.title}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 1,
                  y: {duration: 0.5},
                }}>
                <div className={styles.blind}>
                  구독 자동 결제가 처음이시라면? 첫 달 구독료 할인
                  <br />
                  생애 최초 자동결제 시 1개월 구독권 할인!
                </div>
              </motion.h3>
              <motion.div
                className={styles.ticket_tier1}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 0.5,
                  delay: 0.2,
                  y: {duration: 0.5},
                }}>
                <div className={styles.blind}>
                  <dl>
                    <dt>
                      티어1 30일 구독 자동결제<span>26%</span>
                    </dt>
                    <dd>
                      <del>4,500원</del>
                      3,300원
                    </dd>
                  </dl>
                </div>
                <button
                  type='button'
                  className={styles.join_tier1}
                  onClick={() => handleBuySubscribe(1)}>
                  티어1 구독 할인 받고 가입하기
                </button>
              </motion.div>
              <motion.div
                className={styles.ticket_tier2}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 1,
                  delay: 0.2,
                  y: {duration: 0.5},
                }}>
                <div className={styles.blind}>
                  <dl>
                    <dt>
                      티어2 30일 구독 자동결제<span>28%</span>
                    </dt>
                    <dd>
                      <del>14,500원</del>
                      10,500원
                    </dd>
                    <dd></dd>
                  </dl>
                </div>
                <button
                  type='button'
                  className={styles.join_tier2}
                  onClick={() => handleBuySubscribe(2)}>
                  티어2 구독 할인 받고 가입하기
                </button>
              </motion.div>
            </div>
          </section>
          <section
            className={styles.section_3}
            ref={(element) => {
              subscriptionEventSectionRef.current[2] = element;
            }}>
            <div className={styles.inner}>
              <motion.h3
                className={styles.title}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 1,
                  y: {duration: 0.5},
                }}>
                <div className={styles.blind}>
                  구독 자동결제 재구독 이벤트!
                  <br />
                  자동결제가 해지되었나요?
                  <br />
                  지금 자동결제로 다시 구독하면 첫 달 할인 제공
                </div>
              </motion.h3>
              <motion.div
                className={styles.img_reGudok_box}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 1,
                  delay: 0.2,
                  y: {duration: 0.5},
                }}>
                구독자동결제 재구독<i className={styles.esset}></i>
              </motion.div>
              <motion.dl
                className={styles.reGudok_info}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 1,
                  delay: 0.2,
                  y: {duration: 0.5},
                }}>
                <dt>이벤트 참여 대상</dt>
                <dd>
                  구독 자동결제를 이용 중이었던 회원 중 구독 자동결제가
                  <strong>해지되었거나 해지 예정된 회원</strong>
                </dd>
                <dt>혜택</dt>
                <dd>
                  첫 달 할인된 가격으로 구독 자동결제 이용 가능
                  <dl>
                    <dt>티어1</dt>
                    <dd>
                      구독 자동결제 1개월(30일) : <strong>3,300원</strong>
                    </dd>
                    <dt>티어2</dt>
                    <dd>
                      구독 자동결제 1개월(30일) : <strong>10,500원</strong>
                    </dd>
                  </dl>
                </dd>
                <dt>참여방법</dt>
                <dd>
                  구독 자동결제가 해지된 스트리머를
                  <strong>구독 자동결제로 재구독</strong>하거나{' '}
                  <strong>해지 예정 상태인 경우 해지 취소</strong>시 자동 혜택
                  적용
                </dd>
              </motion.dl>
            </div>
          </section>

          <section
            className={styles.section_4}
            ref={(element) => {
              subscriptionEventSectionRef.current[3] = element;
            }}>
            <div className={styles.inner}>
              <motion.h3
                className={styles.title}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 1,
                  y: {duration: 0.5},
                }}>
                <div className={styles.blind}>
                  새롭게 업그레이드된 구독혜택을 알아볼까요?
                </div>
              </motion.h3>
              <motion.div
                className={styles.benefit_wrap}
                initial={{opacity: 0, y: 50}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: false}}
                transition={{
                  ease: 'easeInOut',
                  duration: 0.5,
                  delay: 0.3,
                  y: {duration: 0.5},
                }}>
                <div className={styles.swiper_container}>
                  <SoopUiSwiper
                    swiperTag={'ul'}
                    swiperMovingButtonTag={'button'}
                    swiperClass={styles.swiper}
                    eventPageContainerId={`#${styles.body}`}
                    maxSlidesPerView={1}
                    options={{
                      loop: true,
                      autoplay: {
                        delay: 3000,
                      },
                    }}
                    swiperMovingButtonClasses={{
                      buttonPrevClass: `swiper-button-prev ${styles.btn_prev}`,
                      buttonNextClass: `swiper-button-next ${styles.btn_next}`,
                    }}>
                    <li className={styles.function}>
                      <div className={styles.blind}>
                        <dl>
                          <dt>어떤 기능이 추가되었나요?</dt>
                          <dd>마침내 구독에 티어가 새롭게 생겼어요!</dd>
                        </dl>
                      </div>
                    </li>
                    <li className={styles.benefit}>
                      <div className={styles.blind}>
                        <dl>
                          <dt>새로운 혜택 소개!</dt>
                          <dd>티어별 이모티콘을 사용하실 수 있어요!</dd>
                        </dl>
                      </div>
                    </li>
                    <li className={styles.service}>
                      <div className={styles.blind}>
                        <dl>
                          <dt>구독 서비스를 기대해 주세요!</dt>
                          <dd>
                            앞으로 새롭게 추가될 구독 혜택도 많이 기대해 주세요!
                          </dd>
                        </dl>
                      </div>
                    </li>
                  </SoopUiSwiper>
                </div>
              </motion.div>
            </div>
          </section>

          <section className={styles.section_5}>
            <div className={styles.inner}>
              <motion.h3 className={styles.title}>
                <div className={styles.blind}>
                  티어2 구독 요금제가 새롭게 출시됐어요!
                </div>
              </motion.h3>
              <motion.div className={styles.benefit_tier2_wrap}>
                <div className={styles.blind}>
                  <h4>티어2 구독 혜택 영수증</h4>
                  <ul>
                    <li>
                      <em>NEW</em>티어2 구독팬 전용 시그니처 이모티콘
                    </li>
                    <li>
                      <em>NEW</em>티어2 구독팬 전용 예정
                    </li>
                    <li>
                      <em>업데이트 예정</em>티어2 구독팬 전용 퍼스나콘
                    </li>
                    <li>
                      <em>업데이트 예정</em>티어2 구독팬 전용 뱃지
                    </li>
                    <li>구독중인 스트리머 LIVE 및 VOD 광고 제거</li>
                    <li>구독자 전용 VOD</li>
                    <li>장기 구독팬 혜택 제공</li>
                    <li>
                      <strong className={styles.price}>14,500원</strong>
                    </li>
                  </ul>
                </div>
              </motion.div>
              <button
                type='button'
                className={styles.join}
                onClick={() => handleBuySubscribe(2, false)}>
                지금 티어2 구독 가입하러가기
              </button>
            </div>
          </section>

          <section className={styles.section_6}>
            <div className={styles.inner}>
              <ul className={styles.noti_wrap}>
                <li
                  className={`${activeGuideLines === 0 ? styles.active : ''}`}>
                  <div className={styles.title_box}>
                    <button
                      type='button'
                      onClick={handleActiveGuideLine}
                      value={0}>
                      <em>이벤트 공통</em> 유의사항
                    </button>
                  </div>
                  <motion.div
                    className={styles.desc_box}
                    animate={{
                      height: activeGuideLines === 0 ? 'auto' : 0,
                      opacity: activeGuideLines === 0 ? 1 : 0,
                    }}>
                    <ul>
                      <li>
                        비정상적인 경로로 이벤트 참여 시 대상자에서 제외됩니다.
                      </li>
                      <li>
                        경품이 모두 소진된 경우 이벤트가 조기 마감될 수
                        있습니다.
                      </li>
                    </ul>
                  </motion.div>
                </li>
                <li
                  className={`${activeGuideLines === 1 ? styles.active : ''}`}>
                  <div className={styles.title_box}>
                    <button
                      type='button'
                      onClick={handleActiveGuideLine}
                      value={1}>
                      <em>2+1 구독 이벤트</em> 유의사항
                    </button>
                  </div>
                  <motion.div
                    className={styles.desc_box}
                    initial={{height: 0, opacity: 0}}
                    animate={{
                      height: activeGuideLines === 1 ? 'auto' : 0,
                      opacity: activeGuideLines === 1 ? 1 : 0,
                    }}
                    transition={{duration: 0.3}}
                    style={{overflow: 'hidden'}}>
                    <ul>
                      <li>이벤트는 1인당 최대 5회까지 참여할 수 있습니다.</li>
                      <li>
                        신규 구독한 스트리머는 구독 정기권과 자동결제가 포함되며
                        구독 선물권은 포함되지 않습니다.
                      </li>
                      <li>구독 선물권은 매주 월요일 일괄 지급됩니다.</li>
                      <li>
                        지급된 구독 선물권의 유효기간은 지급 시점부터
                        1개월입니다.
                      </li>
                      <li>
                        선착순 1만개가 모두 소진된 경우에는 이벤트가 조기 종료될
                        수 있습니다.
                      </li>
                    </ul>
                  </motion.div>
                </li>
                <li
                  className={`${activeGuideLines === 2 ? styles.active : ''}`}>
                  <div className={styles.title_box}>
                    <button
                      type='button'
                      onClick={handleActiveGuideLine}
                      value={2}>
                      <em>생애 최초 구독 자동결제 할인</em> 유의사항
                    </button>
                  </div>
                  <motion.div
                    className={styles.desc_box}
                    initial={{height: 0, opacity: 0}}
                    animate={{
                      height: activeGuideLines === 2 ? 'auto' : 0,
                      opacity: activeGuideLines === 2 ? 1 : 0,
                    }}
                    transition={{duration: 0.3}}
                    style={{overflow: 'hidden'}}>
                    <ul>
                      <li>
                        이벤트는 기간 내 구독 자동결제를 처음으로 이용하시는
                        경우 참여하실 수 있습니다.
                      </li>
                      <li>혜택은 이벤트 기간 내 최초 1회만 제공됩니다.</li>
                      <li>티어1과 티어2 할인 중 1회만 받으실 수 있습니다.</li>
                      <li>
                        가입 후 환불하신 경우에는 혜택을 다시 받으실 수
                        없습니다.
                      </li>
                    </ul>
                  </motion.div>
                </li>
                <li
                  className={`${activeGuideLines === 3 ? styles.active : ''}`}>
                  <div className={styles.title_box}>
                    <button
                      type='button'
                      onClick={handleActiveGuideLine}
                      value={3}>
                      <em>구독 자동결제 재구독 이벤트</em> 유의사항
                    </button>
                  </div>
                  <motion.div
                    className={styles.desc_box}
                    initial={{height: 0, opacity: 0}}
                    animate={{
                      height: activeGuideLines === 3 ? 'auto' : 0,
                      opacity: activeGuideLines === 3 ? 1 : 0,
                    }}
                    transition={{duration: 0.3}}
                    style={{overflow: 'hidden'}}>
                    <ul>
                      <li>
                        과거 구독 자동결제, 구독 선물권, 구독 정기권 이용 이력이
                        있는 스트리머를 자동결제로 구독하시는 경우 혜택이
                        제공됩니다.
                      </li>
                      <li>
                        혜택은 자동으로 적용되며 이벤트 대상자가 아닌 경우
                        혜택이 제공되지 않습니다.
                      </li>
                      <li>
                        티어 1 구독 자동결제의 경우 첫 달 3,300원 혜택이
                        제공되며 이후 4,500원이 결제됩니다.
                      </li>
                      <li>
                        티어 2 구독 자동결제의 경우 첫 달 10,500원 혜택이
                        제공되며 이후 14,500원이 결제됩니다.
                      </li>
                      <li>
                        자동결제 해지 취소는 PC 기준 구독 선물·후원 구독
                        정보에서 진행하실 수 있어요.
                      </li>
                      <li>
                        구독 정기권, 구독 선물권 구매 시 첫 달 할인 혜택은
                        제공되지 않습니다.
                      </li>
                      <li>
                        자동결제로 구독 연장 시 첫 달 할인 혜택은 제공되지
                        않습니다.
                      </li>
                    </ul>
                  </motion.div>
                </li>
              </ul>
            </div>
          </section>
        </div>

        <div
          className={`${styles.layer_modal_wrap} ${layerOpen ? styles.active : ''}`}>
          <div className={styles.flex_box}>
            <div className={styles.modal_contents}>
              <div className={styles.alert_layer}>
                <div className={styles.contents}>
                  <p dangerouslySetInnerHTML={{__html: layerMessage}}></p>
                </div>
                <div className={styles.btn_box}>
                  <button type='button' onClick={() => setLayerOpen(false)}>
                    확인
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  );
};

export default NewSubscribe;
